import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { ArrowLeft, Wallet, Store, ShoppingCart, Package, Download, Upload, Plus, Trash2, Eye, EyeOff, Edit, Calendar } from "lucide-react";
import { useReactToPrint } from "react-to-print";
import CryptoJS from "crypto-js";
import MarkupSettingModal from "../../components/MarkupSettingModal";
import OrderCutoffScheduleModal from "../../components/OrderCutoffScheduleModal";
import EditMerchantInventoryModal from "../../components/EditMerchantInventoryModal";
import AddItemModal from "../../components/AddItemModal";
import AddInventoryItemModal from "../../components/AddInventoryItemModal";
import { adminServices } from "../../services/AdminServices";
import smallNoImage from "../../icons/inventory/item-master/small-noimage.png";
import { validatePassword } from "../../tools/passwordPolicy";
const MerchantDetail = ({
  merchant,
  onBack
}) => {
  const [merchantDetail, setMerchantDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topSellingItems, setTopSellingItems] = useState([]);
  const [topSellingLoading, setTopSellingLoading] = useState(false);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [isActive, setIsActive] = useState(merchant?.status === "active");
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [orderFilter, setOrderFilter] = useState("All");
  const [importingCsv, setImportingCsv] = useState(false);
  const [isMarkupModalOpen, setIsMarkupModalOpen] = useState(false);
  const [inventoryData, setInventoryData] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryUpdating, setInventoryUpdating] = useState(null);
  const [inventoryTablePage, setInventoryTablePage] = useState(1);
  const [editingInventoryItem, setEditingInventoryItem] = useState(null);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [isAddInventoryItemModalOpen, setIsAddInventoryItemModalOpen] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [allProductsLoading, setAllProductsLoading] = useState(false);
  const [suggestedItems, setSuggestedItems] = useState([]);
  const [suggestedItemsLoading, setSuggestedItemsLoading] = useState(false);
  const [isCutoffModalOpen, setIsCutoffModalOpen] = useState(false);
  const [selectedCutoffDay, setSelectedCutoffDay] = useState(null);
  const [scheduleData, setScheduleData] = useState({});
  const [scheduleEnabledByDay, setScheduleEnabledByDay] = useState({
    Monday: false,
    Tuesday: false,
    Wednesday: false,
    Thursday: false,
    Friday: false,
    Saturday: false
  });
  const [cutoffStatusUpdatingByDay, setCutoffStatusUpdatingByDay] = useState({});
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [inventoryFilter, setInventoryFilter] = useState("ALL"); // ALL | LOW_STOCK | OUT_OF_STOCK
  const inventorySectionRef = useRef(null);
  const printRef = useRef(null);
  const csvFileInputRef = useRef(null);
  const merchantId = merchant?.merchantId ?? merchant?.id ?? merchant?.storeCode;

  // Load merchant cut-off config when entering Merchant Detail
  useEffect(() => {
    if (!merchantId) return;
    adminServices.postMerchantCutoffsInit(merchantId).then(res => {
      const list = Array.isArray(res?.data) ? res.data : [];
      if (!list || list.length === 0) {
        setScheduleData({});
        setScheduleEnabledByDay(prev => ({
          ...prev
        }));
        return;
      }
      const byDay = {};
      const enabledMap = {
        ...scheduleEnabledByDay
      };
      list.forEach(entry => {
        const d = entry?.day || "";
        if (!d) return;
        byDay[d] = entry;
        if (entry?.status === "ACTIVE") {
          enabledMap[d] = true;
        }
      });
      setScheduleData(byDay);
      setScheduleEnabledByDay(enabledMap);
    }).catch(err => {
      console.error("Failed to load merchant cut-offs:", err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);
  useEffect(() => {
    if (!isAddInventoryItemModalOpen) return;
    if (allProductsLoading) return;
    if (Array.isArray(allProducts) && allProducts.length > 0) return;
    fetchAllProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddInventoryItemModalOpen]);
  useEffect(() => {
    setIsActive(merchant?.status === "active");
  }, [merchant?.status]);
  const handleStatusToggle = nextChecked => {
    if (!merchantId) return;
    if (statusUpdating) return;
    const nextStatusId = nextChecked ? 1 : 0;
    const prevChecked = isActive;
    setIsActive(nextChecked);
    setStatusUpdating(true);
    adminServices.setMerchantStatusV2(nextStatusId, merchantId).catch(err => {
      console.error("Failed to update merchant status:", err);
      setIsActive(prevChecked);
      window.alert("Failed to update merchant status. Please try again.");
    }).finally(() => setStatusUpdating(false));
  };
  const handleNoScheduleToggle = (day, nextChecked) => {
    const prevChecked = !!scheduleEnabledByDay[day];
    const rawEntry = scheduleData?.[day];
    const cutOffId = rawEntry?.cutOffId ?? rawEntry?.cutoffId ?? rawEntry?.id;

    // Optimistic UI
    setScheduleEnabledByDay(prev => ({
      ...prev,
      [day]: nextChecked
    }));
    setScheduleData(prev => {
      const existing = prev?.[day];
      if (!existing || typeof existing !== "object") return prev;
      return {
        ...prev,
        [day]: {
          ...existing,
          status: nextChecked ? "ACTIVE" : "INACTIVE"
        }
      };
    });

    // If we don't have an existing cutOffId yet, just keep UI change (status will be persisted on save)
    if (!cutOffId) return;
    if (cutoffStatusUpdatingByDay?.[day]) return;
    setCutoffStatusUpdatingByDay(prev => ({
      ...prev,
      [day]: true
    }));
    adminServices.updateCutOffStatus(cutOffId, nextChecked ? "ACTIVE" : "INACTIVE").catch(err => {
      console.error("Failed to update cut-off status:", err);
      setScheduleEnabledByDay(prev => ({
        ...prev,
        [day]: prevChecked
      }));
      setScheduleData(prev => {
        const existing = prev?.[day];
        if (!existing || typeof existing !== "object") return prev;
        return {
          ...prev,
          [day]: {
            ...existing,
            status: prevChecked ? "ACTIVE" : "INACTIVE"
          }
        };
      });
      window.alert("Failed to update cut-off status. Please try again.");
    }).finally(() => setCutoffStatusUpdatingByDay(prev => ({
      ...prev,
      [day]: false
    })));
  };
  const closeChangePassword = () => {
    setIsChangePasswordOpen(false);
    setNewPassword("");
    setConfirmNewPassword("");
    setPasswordSubmitting(false);
    setShowNewPassword(false);
    setShowConfirmNewPassword(false);
  };
  const passwordMismatch = newPassword.length > 0 && confirmNewPassword.length > 0 && newPassword !== confirmNewPassword;
  const sha256Hex = text => {
    return CryptoJS.SHA256(String(text)).toString();
  };
  const handleChangePassword = async e => {
    e?.preventDefault?.();
    if (passwordSubmitting) return;
    if (!newPassword || !confirmNewPassword) return;
    if (passwordMismatch) return;
    const passwordCheck = validatePassword(newPassword);
    if (!passwordCheck.valid) {
      window.alert(passwordCheck.message);
      return;
    }
    const posUserId = merchantDetail?.posUserId ?? merchantDetail?.posUserID;
    const posUserName = merchantDetail?.posUserName ?? merchantDetail?.posUsername ?? merchantDetail?.posUser;
    if (!posUserId || !posUserName) {
      window.alert("POS account not found for this merchant.");
      return;
    }
    setPasswordSubmitting(true);
    try {
      const hashed = sha256Hex(newPassword);
      // Format matches SP requirement: 2,'toto','<sha256hex>'

      await adminServices.storeProcedure2("UpdatePostAccount", {
        p_id: posUserId,
        p_uname: posUserName,
        p_pass: hashed
      });
      window.alert("Password updated successfully.");
      closeChangePassword();
    } catch (err) {
      console.error("Failed to update POS account password:", err);
      window.alert("Failed to update password. Please try again.");
      setPasswordSubmitting(false);
    }
  };
  const handleAddItems = items => {
    if (!merchantId || !items?.length) return;
    const productIds = items.map(i => i.productId ?? i.inventoryId ?? i.id).filter(Boolean);
    if (productIds.length === 0) return;
    adminServices.suggestMerchantProducts(merchantId, productIds).then(() => {
      const newItems = items.map(it => {
        const invId = it.inventoryId ?? it.id;
        const prodId = it.productId ?? invId;
        return {
          id: invId,
          inventoryId: invId,
          productId: prodId,
          name: it.description ?? it.item ?? "-",
          sku: it.sku ?? "-",
          price: it.price != null ? typeof it.price === "number" ? `₱${it.price.toLocaleString("en-PH", {
            minimumFractionDigits: 2
          })}` : it.price : "-",
          unit: it.uom ?? "Box",
          image: smallNoImage
        };
      });
      setSuggestedItems(prev => [...prev, ...newItems.filter(n => !prev.some(p => (p.productId ?? p.inventoryId ?? p.id) === n.productId))]);
    }).catch(err => console.error("Failed to suggest products:", err)).finally(() => {});
  };
  const handleUnsuggestProduct = productId => {
    if (!merchantId || productId == null) return;
    adminServices.unsuggestMerchantProduct(merchantId, productId).then(() => setSuggestedItems(prev => prev.filter(p => (p.productId ?? p.inventoryId ?? p.id) !== productId))).catch(err => console.error("Failed to unsuggest product:", err));
  };
  const fetchAllProducts = async () => {
    setAllProductsLoading(true);
    try {
      const res = await adminServices.getData("products/data");
      const raw = Array.isArray(res?.data) ? res.data : [];
      const mapped = raw.map((p, idx) => ({
        id: p.id ?? idx + 1,
        productId: p.id ?? p.productId,
        sku: p.sku ?? p.productSku ?? "-",
        description: p.description ?? p.name ?? p.productName ?? "-",
        cost: p.marketPrice ?? p.cost ?? p.price ?? 0,
        // Prefer largest UOM for inventory creation
        uom: p.largestUOM ?? p.smallestUOM ?? p.stockUom ?? p.uom ?? "",
        largestUOM: p.largestUOM,
        largestUOMId: p.largestUOMId ?? p.largestUomId ?? p.largest_uom_id,
        originalData: p
      }));
      setAllProducts(mapped);
    } catch (err) {
      console.error("Failed to fetch products:", err);
      setAllProducts([]);
    } finally {
      setAllProductsLoading(false);
    }
  };
  const handleAddInventoryItems = async items => {
    if (!merchantId || !items?.length) return;
    try {
      const productsPayload = items.map(i => {
        const src = i?.originalData ?? i ?? {};
        const id = src.id ?? src.productId ?? i.id ?? i.productId;
        const sku = src.sku ?? src.productSku ?? i.sku ?? i.productSku;
        if (id == null || !String(sku || "").trim()) return null;
        const marketPriceRaw = src.marketPrice ?? src.price ?? src.cost ?? i.marketPrice ?? i.price ?? i.cost ?? 0;
        const profitMarginRaw = src.profitMargin ?? src.margin ?? src.profit_margin ?? 0;
        return {
          id: Number(id) || id,
          sku: String(sku || "").trim(),
          barcode: src.barcode ?? src.Barcode ?? "",
          description: src.description ?? src.name ?? src.productName ?? i.description ?? i.item ?? "",
          category: src.category ?? src.categoryName ?? "",
          classification: src.classification ?? "",
          subCategory: src.subCategory ?? src.subclassification ?? src.subCategoryName ?? "",
          brand: src.brand ?? "",
          variant: src.variant ?? "",
          largestUOM: src.largestUOM ?? src.largestUom ?? i.largestUOM ?? "",
          smallestUOM: src.smallestUOM ?? src.smallestUom ?? "",
          marketPrice: Number(marketPriceRaw) || 0,
          profitMargin: Number(profitMarginRaw) || 0,
          settings: src.settings ?? null
        };
      }).filter(Boolean);
      if (productsPayload.length === 0) return;
      await adminServices.addMerchantInventoryProducts(merchantId, productsPayload);
      await loadMerchantInventory();
    } catch (err) {
      console.error("Failed to add inventory items:", err);
      window.alert("Failed to add item(s). Please try again.");
    }
  };
  const handleInventoryUpdate = (row, payload) => {
    const inventoryId = row.inventoryId ?? row.id;
    setInventoryUpdating(inventoryId);
    adminServices.updateMerchantInventory(inventoryId, payload).then(() => {
      setInventoryData(prev => prev.map(r => {
        if ((r.inventoryId ?? r.id) !== inventoryId) return r;
        const qty = payload.stockAmount ?? r.qty;
        const critical = payload.criticalLevel ?? r.criticalLevel ?? 0;
        const availability = qty === 0 ? "Out of Stock" : critical > 0 && qty <= critical ? "Low Stock" : "In Stock";
        return {
          ...r,
          qty,
          criticalLevel: payload.criticalLevel ?? r.criticalLevel,
          replenishAmount: payload.replenishAmount ?? r.replenishAmount,
          autoReplenishment: payload.isAutoReplenishEnabled ?? r.autoReplenishment,
          availability
        };
      }));
    }).catch(err => {
      console.error("Failed to update inventory:", err);
    }).finally(() => setInventoryUpdating(null));
  };
  const handleInventoryStatusChange = (row, nextStatus) => {
    const productId = row.productId ?? row.originalData?.productId;
    if (!merchantId || productId == null) return;
    const invKey = row.inventoryId ?? row.id;
    if (inventoryUpdating === invKey) return;
    const normalized = String(nextStatus || "").toUpperCase();
    if (normalized !== "ACTIVE" && normalized !== "INACTIVE") return;
    const prevStatus = row.status;
    setInventoryUpdating(invKey);
    setInventoryData(prev => prev.map(r => (r.inventoryId ?? r.id) === invKey ? {
      ...r,
      status: normalized
    } : r));
    adminServices.updateData("MerchantInventory/status", {
      merchantId: Number(merchantId) || merchantId,
      productId: Number(productId) || productId,
      status: normalized
    }).catch(err => {
      console.error("Failed to update merchant inventory status:", err);
      setInventoryData(prev => prev.map(r => (r.inventoryId ?? r.id) === invKey ? {
        ...r,
        status: prevStatus
      } : r));
      window.alert("Failed to update item status. Please try again.");
    }).finally(() => setInventoryUpdating(null));
  };
  useEffect(() => {
    if (!merchantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    adminServices.getMerchantById(merchantId, {
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined
    }).then(res => {
      const data = res?.data;
      if (data) setMerchantDetail(data);
    }).catch(err => {
      console.error("Failed to fetch merchant detail:", err);
    }).finally(() => setLoading(false));
  }, [merchantId, dateFrom, dateTo]);
  useEffect(() => {
    if (!merchantId) return;
    setTopSellingLoading(true);
    adminServices.getMerchantTopSellingProducts(merchantId, 1, 10).then(res => {
      const raw = Array.isArray(res?.data) ? res.data : res?.data?.data ?? res?.data?.items ?? [];
      const mapped = raw.map((p, idx) => ({
        id: p.id ?? idx + 1,
        name: p.productName ?? p.name ?? p.itemName ?? "",
        sold: p.numSold ?? p.sold ?? p.quantity ?? p.totalSold ?? 0,
        price: p.price ?? p.srp ?? p.cost ?? "",
        image: p.image ?? p.imageUrl ?? smallNoImage
      }));
      setTopSellingItems(mapped);
    }).catch(err => {
      console.error("Failed to fetch top selling products:", err);
      setTopSellingItems([]);
    }).finally(() => setTopSellingLoading(false));
  }, [merchantId]);
  useEffect(() => {
    if (!merchantId) return;
    setTransactionLoading(true);
    adminServices.getMerchantTransactionHistory(merchantId, 1, 10).then(res => {
      const raw = Array.isArray(res?.data) ? res.data : res?.data?.data ?? res?.data?.items ?? res?.data?.transactions ?? [];
      const mapped = raw.map((t, idx) => {
        const amount = t.amount ?? t.total ?? t.value ?? 0;
        const isNeg = Number(amount) < 0;
        const amtStr = typeof amount === "number" ? `${isNeg ? "-" : ""}PHP ${Math.abs(amount).toLocaleString("en-PH", {
          minimumFractionDigits: 2
        })}` : t.amountFormatted ?? String(amount);
        return {
          id: t.id ?? idx + 1,
          itemName: t.itemName ?? t.description ?? t.productName ?? t.reference ?? "-",
          transactionId: t.transactionId ?? t.transactionNo ?? t.reference ?? t.id ?? "",
          amount: amtStr,
          date: t.date ? new Date(t.date).toLocaleString() : t.createdAt ?? t.transactionDate ?? "",
          isNegative: isNeg
        };
      });
      setTransactionHistory(mapped);
    }).catch(err => {
      console.error("Failed to fetch transaction history:", err);
      setTransactionHistory([]);
    }).finally(() => setTransactionLoading(false));
  }, [merchantId]);
  const loadMerchantInventory = useCallback(async () => {
    if (!merchantId) return;
    setInventoryLoading(true);
    try {
      const res = await adminServices.getMerchantInventory(merchantId, 1, 10);
      const raw = Array.isArray(res?.data) ? res.data : res?.data?.items ?? res?.data?.data ?? [];
      const mapped = raw.map((r, idx) => {
        const stock = Number(r.stockAmount ?? r.stock ?? r.qty ?? 0) || 0;
        const critical = Number(r.criticalLevel ?? r.critical ?? 0) || 0;
        const replenish = Number(r.maxLevel ?? r.replenishAmount ?? r.replenish ?? 0) || 0;
        const availability = stock === 0 ? "Out of Stock" : critical > 0 && stock <= critical ? "Low Stock" : "In Stock";
        const inventoryId = r.inventoryId ?? r.id;
        return {
          id: inventoryId != null ? `${inventoryId}-${idx}` : `inv-${idx}`,
          inventoryId,
          productId: r.productId,
          autoReplenishment: r.isAutoReplenishEnabled ?? r.autoReplenishment ?? r.auto_replenishment ?? true,
          criticalLevel: critical,
          replenishAmount: replenish,
          sku: r.productSku ?? r.sku ?? r.productCode ?? "",
          item: r.productName ?? r.item ?? r.itemName ?? r.name ?? "",
          qty: stock,
          category: r.category ?? r.categoryName ?? "",
          cost: r.cost != null ? r.cost : "",
          uom: r.stockUom ?? r.uom ?? r.unit ?? "",
          srp: r.srp != null ? r.srp : "",
          availability,
          status: r.status ?? "ACTIVE",
          maxLevel: r.maxLevel ?? replenish
        };
      });
      setInventoryData(mapped);
    } catch (err) {
      console.error("Failed to fetch merchant inventory:", err);
      setInventoryData([]);
    } finally {
      setInventoryLoading(false);
    }
  }, [merchantId]);
  useEffect(() => {
    loadMerchantInventory();
  }, [loadMerchantInventory]);
  useEffect(() => {
    if (!merchantId) return;
    setSuggestedItemsLoading(true);
    adminServices.getSuggestedProducts(merchantId).then(res => {
      const raw = Array.isArray(res?.data) ? res.data : res?.data?.data ?? res?.data?.items ?? [];
      const mapped = raw.map((p, idx) => {
        const invId = p.inventoryId ?? p.id ?? idx + 1;
        const priceVal = p.cost ?? p.price ?? p.srp ?? 0;
        const priceStr = typeof priceVal === "number" ? `₱${priceVal.toLocaleString("en-PH", {
          minimumFractionDigits: 2
        })}` : p.priceFormatted ?? (priceVal ? String(priceVal) : "-");
        return {
          id: invId,
          inventoryId: invId,
          productId: p.productId,
          name: p.productName ?? p.item ?? p.name ?? "-",
          sku: p.productSku ?? p.sku ?? p.productCode ?? (p.productId ? String(p.productId) : "-"),
          price: priceStr,
          unit: p.stockUomName ?? p.stockUom ?? p.uom ?? p.unit ?? "Box",
          image: p.imageUrl ?? p.image ?? smallNoImage
        };
      });
      setSuggestedItems(mapped);
    }).catch(err => {
      console.error("Failed to fetch suggested products:", err);
      setSuggestedItems([]);
    }).finally(() => setSuggestedItemsLoading(false));
  }, [merchantId]);

  // Order history from API
  const orderHistoryRaw = merchantDetail?.orderHistory ?? [];
  const orderHistory = orderHistoryRaw.map((o, idx) => ({
    id: idx + 1,
    orderNumber: o.orderNo ?? o.orderNumber ?? "",
    date: o.regDate ? new Date(o.regDate).toLocaleString() : "",
    status: o.status ?? ""
  }));
  const filteredOrderHistory = useMemo(() => {
    if (!Array.isArray(orderHistory)) return [];
    if (orderFilter === "All") return orderHistory;
    const key = String(orderFilter || "").toUpperCase();
    return orderHistory.filter(o => String(o.status || "").toUpperCase() === key);
  }, [orderHistory, orderFilter]);
  const PERFORMANCE_COLORS = ["#FF928A", "#8979FF", "#3CC3DF"];
  const performanceData = (topSellingItems || []).slice(0, 3).map((p, idx) => ({
    name: p.name ?? p.productName ?? p.itemName ?? "Item",
    value: Number(p.sold ?? p.numSold ?? p.numSales ?? 0) || 0,
    color: p.color ?? PERFORMANCE_COLORS[idx % PERFORMANCE_COLORS.length]
  }));
  const highestNumSales = performanceData.length ? Math.max(...performanceData.map(d => d.value), 0) : 0;
  const performanceScaleMax = Math.max(100, highestNumSales);
  const formatCurrency = val => {
    if (val == null || val === "") return "-";
    const n = Number(val);
    if (isNaN(n)) return String(val);
    return `₱${n.toLocaleString("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };
  const getStatusBadge = status => {
    const s = String(status ?? "").toUpperCase();
    let label = "Active";
    let badgeClass = "merchant-status-badge";
    if (s === "ACTIVE") {
      label = "Activate";
      badgeClass += " active";
    } else if (s === "INACTIVE") {
      label = "Inactive";
      badgeClass += " inactive";
    } else if (s === "PENDING") {
      label = "Pending";
      badgeClass += " pending";
    } else if (s === "DISCONTINUED") {
      label = "Discon";
      badgeClass += " discontinued";
    } else if (s === "UNAVAILABLE") {
      label = "Unav";
      badgeClass += " unavailable";
    } else {
      badgeClass += " active";
    }
    return {
      label,
      badgeClass
    };
  };

  // Inventory table: filter, dedupe and paginate (10 rows per page)
  const inventoryDisplayLimit = 10;
  const inventoryFilteredData = useMemo(() => {
    const base = Array.isArray(inventoryData) ? inventoryData : [];
    if (inventoryFilter === "LOW_STOCK") {
      return base.filter(row => String(row.availability) === "Low Stock");
    }
    if (inventoryFilter === "OUT_OF_STOCK") {
      return base.filter(row => String(row.availability) === "Out of Stock");
    }
    if (inventoryFilter === "IN_STOCK") {
      return base.filter(row => String(row.availability) === "In Stock");
    }
    return base;
  }, [inventoryData, inventoryFilter]);
  const inventoryUniqueData = (() => {
    const seen = new Set();
    const unique = [];
    for (const row of inventoryFilteredData) {
      const key = `${row?.inventoryId ?? ""}-${row?.productId ?? ""}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(row);
    }
    return unique;
  })();
  const inventoryTotalPages = Math.max(1, Math.ceil((inventoryUniqueData.length || 0) / inventoryDisplayLimit));
  const inventorySafePage = Math.min(Math.max(1, Number(inventoryTablePage) || 1), inventoryTotalPages);
  const inventoryDisplayData = inventoryUniqueData.slice((inventorySafePage - 1) * inventoryDisplayLimit, inventorySafePage * inventoryDisplayLimit);
  const inventoryTotalCount = inventoryUniqueData.length || 0;
  const inventoryRangeStart = inventoryTotalCount === 0 ? 0 : (inventorySafePage - 1) * inventoryDisplayLimit + 1;
  const inventoryRangeEnd = Math.min(inventorySafePage * inventoryDisplayLimit, inventoryTotalCount);

  // Fill to minimum 10 rows for consistent layout (same pattern as MerchantManagement)
  const minInventoryRows = inventoryDisplayLimit;
  const inventoryFilledData = inventoryDisplayData.length > 0 ? (() => {
    const filled = [...inventoryDisplayData];
    while (filled.length < minInventoryRows) {
      filled.push({
        id: `placeholder-${filled.length}`,
        _placeholder: true
      });
    }
    return filled;
  })() : [];

  // CSV import helpers
  const CSV_TEMPLATE_HEADERS = ["sku", "barcode", "description", "category", "classification", "subCategory", "brand", "variant", "largestUOM", "smallestUOM", "marketPrice", "profitMargin"];
  const handleDownloadCsvTemplate = () => {
    const csv = [CSV_TEMPLATE_HEADERS.join(",")].join("\n");
    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "merchant_inventory_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const parseCsvLine = line => {
    const result = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };
  const handleCsvFileChange = async e => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !merchantId) return;
    setImportingCsv(true);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) {
        alert("CSV file is empty or missing data rows.");
        return;
      }
      const headers = parseCsvLine(lines[0]).map(h => h.toLowerCase().trim());
      const skuIdx = headers.indexOf("sku");
      const descIdx = headers.indexOf("description");
      if (skuIdx === -1 || descIdx === -1) {
        alert("CSV must contain 'sku' and 'description' columns.");
        return;
      }
      const products = lines.slice(1).map(line => {
        const cols = parseCsvLine(line);
        const get = name => {
          const idx = headers.indexOf(name.toLowerCase());
          return idx >= 0 ? cols[idx] : "";
        };
        return {
          id: 0,
          sku: get("sku"),
          barcode: get("barcode"),
          description: get("description"),
          category: get("category"),
          classification: get("classification"),
          subCategory: get("subcategory"),
          brand: get("brand"),
          variant: get("variant"),
          largestUOM: get("largestuom") || get("largestUOM"),
          smallestUOM: get("smallestuom") || get("smallestUOM"),
          marketPrice: Number(get("marketprice") || get("marketPrice")) || 0,
          profitMargin: Number(get("profitmargin") || get("profitMargin")) || 0,
          settings: null
        };
      }).filter(p => p.sku && p.description);
      if (products.length === 0) {
        alert("No valid products found in CSV.");
        return;
      }
      await adminServices.addMerchantInventoryProducts(merchantId, products);
      alert(`Imported ${products.length} product(s) successfully.`);
      await loadMerchantInventory();
    } catch (error) {
      console.error("CSV import failed:", error);
      alert(error.response?.data?.message || "Failed to import CSV.");
    } finally {
      setImportingCsv(false);
    }
  };

  // Merge row data with API response for display
  const merchantData = {
    merchantName: merchantDetail?.merchantName ?? merchant?.merchantName ?? "",
    storeCode: merchantDetail?.storeCode ?? merchant?.storeCode ?? "",
    facility: merchantDetail?.facilityName ?? merchant?.facility ?? "",
    location: merchantDetail?.location ?? merchant?.location ?? "",
    pdlNumber: merchantDetail?.pdlNumber ?? merchant?.pdlNumber ?? "",
    pdlName: merchantDetail?.pdlFullName ?? merchant?.pdlName ?? "",
    status: merchant?.status ?? merchantDetail?.status ?? "",
    walletBalance: merchantDetail?.walletBalance ?? 0,
    totalSales: merchantDetail?.totalSales ?? 0,
    completedOrders: merchantDetail?.completedOrders ?? 0,
    pendingOrders: merchantDetail?.pendingOrders ?? 0,
    lowStocks: merchantDetail?.lowStocks ?? 0
  };

  // Print / PDF export
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Merchant_${merchantData?.merchantName || merchantId || "Detail"}`,
    onPrintError: err => console.error("Print failed:", err)
  });
  const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const scrollToInventory = () => {
    if (inventorySectionRef.current) {
      inventorySectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };
  return <div className="dashboard-content">
      <div className="merchant-detail-page">
        <div ref={printRef} className="merchant-detail-print-area">
        {/* Order Cut-off Schedule – top section with back + title only */}
        <div className="merchant-detail-header" style={{
          marginBottom: 0
        }}>
          <button className="merchant-detail-back-btn" onClick={onBack}>
            <ArrowLeft size={20} />
          </button>
          
        </div>
        <h1 className="merchant-detail-title" style={{
          margin: 0,
          flex: 1,
          marginTop: 20
        }}>
            Order Cut-off Schedule
          </h1>
        {/* Order Cut-off day cards */}
        <div style={{
          marginBottom: "32px"
        }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: "16px"
          }}>
            {DAYS.map(day => {
              const rawEntry = scheduleData[day];
              let dayItems = [];
              if (Array.isArray(rawEntry)) {
                dayItems = rawEntry;
              } else if (rawEntry && typeof rawEntry === "object") {
                if (Array.isArray(rawEntry.items)) {
                  dayItems = rawEntry.items;
                } else if (typeof rawEntry.items === "string" && rawEntry.items.trim()) {
                  try {
                    let parsed = JSON.parse(rawEntry.items);
                    // Handle double-encoded case: items is a JSON string of a JSON string
                    if (typeof parsed === "string") {
                      parsed = JSON.parse(parsed);
                    }
                    if (Array.isArray(parsed)) {
                      dayItems = parsed;
                    }
                  } catch (e) {
                    console.error("Failed to parse merchant cutoff items for", day, e);
                  }
                }
              }
              // Sort items by time (ascending) when time is present
              dayItems = [...dayItems].sort((a, b) => {
                const ta = a?.time || "";
                const tb = b?.time || "";
                if (!ta && !tb) return 0;
                if (!ta) return 1;
                if (!tb) return -1;
                return ta.localeCompare(tb);
              });
              const enabled = scheduleEnabledByDay[day];
              return <div key={day} style={{
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                padding: "16px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                minHeight: 200
              }}>
                  <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "12px"
                }}>
                    <h3 style={{
                    fontSize: "1rem",
                    fontWeight: "600",
                    margin: 0,
                    color: "#111827"
                  }}>{day}</h3>
                    <button onClick={() => {
                    setSelectedCutoffDay(day);
                    setIsCutoffModalOpen(true);
                  }} style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                      <Edit size={16} color="#6b7280" />
                    </button>
                  </div>
                  <div style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  maxHeight: 8 * 24,
                  // roughly 8 rows
                  overflowY: "auto"
                }}>
                    {dayItems.length === 0 ? <span style={{
                    fontSize: "0.875rem",
                    color: "#9ca3af"
                  }}>No items</span> : dayItems.map((item, idx) => {
                    const name = item?.item_name ?? item?.name ?? String(item ?? "");
                    const time = item?.time ?? "";
                    return <div key={idx} style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: "0.875rem",
                      color: "#374151"
                    }}>
                            <span>{name || "-"}</span>
                            <span style={{
                        color: "#6b7280"
                      }}>{time || ""}</span>
                          </div>;
                  })}
                  </div>
                  <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: "12px",
                  paddingTop: "12px",
                  borderTop: "1px solid #e5e7eb"
                }}>
                    <span style={{
                    fontSize: "0.875rem",
                    color: "#6b7280"
                  }}>No Schedule</span>
                    <label style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    margin: 0
                  }}>
                      <input type="checkbox" checked={enabled} disabled={!!cutoffStatusUpdatingByDay?.[day]} onChange={e => handleNoScheduleToggle(day, e.target.checked)} style={{
                      position: "absolute",
                      opacity: 0,
                      width: 0,
                      height: 0,
                      margin: 0
                    }} />
                      <span style={{
                      width: 40,
                      height: 22,
                      borderRadius: 11,
                      background: enabled ? "#22c55e" : "#d1d5db",
                      position: "relative",
                      transition: "background 0.2s"
                    }}>
                        <span style={{
                        position: "absolute",
                        top: 2,
                        left: enabled ? 20 : 2,
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        background: "#fff",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                        transition: "left 0.2s"
                      }} />
                      </span>
                    </label>
                  </div>
                </div>;
            })}
          </div>
        </div>

        

        {/* Merchant header */}
        <div className="merchant-detail-header">
          <div className="merchant-detail-header-info" style={{
            flex: 1
          }}>
            <h1 className="merchant-detail-title">
              {merchantData.merchantName} ({merchantData.storeCode})
            </h1>
            <div className="merchant-detail-info-row">
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap"
              }}>
                <span className="merchant-detail-info-text">
                  Facility: {merchantData.facility} - Maximum | Location: {merchantData.location}
                </span>
                <div className="merchant-detail-status-toggle" style={{
                  marginTop: 2
                }}>
                  <label className="merchant-detail-toggle-label">
                    <input type="checkbox" checked={isActive} disabled={statusUpdating || !merchantId} onChange={e => handleStatusToggle(e.target.checked)} className="merchant-detail-status-toggle-input" />
                    <span className="merchant-detail-status-toggle-slider"></span>
                    <span className="merchant-detail-status-label-text">Active</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="merchant-detail-info-row">
              <span className="merchant-detail-info-text">
                PDL Info: {merchantData.pdlNumber} - {merchantData.pdlName}
              </span>
            </div>
          </div>
          <div className="merchant-detail-header-actions">
            <div className="merchant-detail-action-buttons">
              <button className="merchant-detail-markup-btn" onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                //console.log("Markup Settings clicked");
                setIsMarkupModalOpen(true);
              }}>
                Markup Settings
              </button>
              <button className="merchant-detail-password-btn" onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setIsChangePasswordOpen(true);
                setNewPassword("");
                setConfirmNewPassword("");
                setShowNewPassword(false);
                setShowConfirmNewPassword(false);
              }}>
                Change Password
              </button>
              <button className="merchant-detail-export-btn" onClick={() => handlePrint()}>
                <Download size={18} />
                Export as PDF
              </button>
            </div>
          </div>
        </div>

                {/* Date range filter – right-aligned above Order History / Performance */}
                <div style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "16px",
          marginBottom: "16px"
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}>
            <Calendar size={18} color="#6b7280" />
            <label style={{
              fontSize: "0.875rem",
              color: "#374151"
            }}>From</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{
              padding: "8px 12px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "0.875rem"
            }} />
          </div>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}>
            <Calendar size={18} color="#6b7280" />
            <label style={{
              fontSize: "0.875rem",
              color: "#374151"
            }}>To</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{
              padding: "8px 12px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "0.875rem"
            }} />
          </div>
        </div>

        {/* Key Metrics */}
        <div className="merchant-detail-metrics">
          <div className="merchant-detail-metric-card">
            <div className="merchant-detail-metric-content">
              <div className="merchant-detail-metric-info">
                <span className="merchant-detail-metric-label">Wallet Balance</span>
                <span className="merchant-detail-metric-value">
                  {loading ? "—" : `PHP ${Number(merchantData.walletBalance).toLocaleString("en-PH", {
                    minimumFractionDigits: 2
                  })}`}
                </span>
              </div>
              <Wallet size={32} className="merchant-detail-metric-icon wallet" />
            </div>
          </div>
          <div className="merchant-detail-metric-card">
            <div className="merchant-detail-metric-content">
              <div className="merchant-detail-metric-info">
                <span className="merchant-detail-metric-label">Total Sales</span>
                <span className="merchant-detail-metric-value">{loading ? "—" : Number(merchantData.totalSales).toLocaleString()}</span>
              </div>
              <Store size={32} className="merchant-detail-metric-icon sales" />
            </div>
          </div>
          <div className="merchant-detail-metric-card">
            <div className="merchant-detail-metric-content">
              <div className="merchant-detail-metric-info">
                <span className="merchant-detail-metric-label">Completed Orders</span>
                <span className="merchant-detail-metric-value">{loading ? "—" : Number(merchantData.completedOrders).toLocaleString()}</span>
              </div>
              <ShoppingCart size={32} className="merchant-detail-metric-icon completed" />
            </div>
          </div>
          <div className="merchant-detail-metric-card">
            <div className="merchant-detail-metric-content">
              <div className="merchant-detail-metric-info">
                <span className="merchant-detail-metric-label">Pending Orders</span>
                <span className="merchant-detail-metric-value">{loading ? "—" : Number(merchantData.pendingOrders).toLocaleString()}</span>
              </div>
              <ShoppingCart size={32} className="merchant-detail-metric-icon pending" />
            </div>
          </div>
          <div className="merchant-detail-metric-card" style={{
            cursor: "pointer"
          }} onClick={() => {
            setInventoryFilter(prev => prev === "LOW_STOCK" ? "ALL" : "LOW_STOCK");
            setInventoryTablePage(1);
            scrollToInventory();
          }}>
            <div className="merchant-detail-metric-content">
              <div className="merchant-detail-metric-info">
                <span className="merchant-detail-metric-label">Low Stocks</span>
                <span className="merchant-detail-metric-value">
                  {loading ? "—" : Number(merchantData.lowStocks).toLocaleString()}
                </span>
              </div>
              <Package size={32} className="merchant-detail-metric-icon low-stock" />
            </div>
          </div>
        </div>

        {/* Order History and Performance */}
        <div className="merchant-detail-sections-row">
          <div className="merchant-detail-section-card">
            <div className="merchant-detail-section-header">
              <h2 className="merchant-detail-section-title">Order History</h2>
              <select className="merchant-detail-filter-select" value={orderFilter} onChange={e => setOrderFilter(e.target.value)}>
                <option value="All">All</option>
                <option value="Pending">Pending</option>
                <option value="Processing">Processing</option>
                <option value="Approved">Approved</option>
                <option value="Voided">Voided</option>
              </select>
            </div>
            <div className="merchant-detail-order-list">
              {filteredOrderHistory.length === 0 ? <div className="merchant-detail-empty-state">No records</div> : filteredOrderHistory.map(order => <div key={order.id} className="merchant-detail-order-item">
                    <div className="merchant-detail-order-info">
                      <span className="merchant-detail-order-number">{order.orderNumber}</span>
                      <span className="merchant-detail-order-date">{order.date}</span>
                    </div>
                    <span className={`merchant-detail-order-status ${(order.status || "").toLowerCase()}`}>
                      {order.status}
                    </span>
                  </div>)}
            </div>
          </div>

          <div className="merchant-detail-section-card">
            <div className="merchant-detail-section-header">
              <h2 className="merchant-detail-section-title">Performance (Top 3 Items)</h2>
            </div>
            <div className="merchant-detail-performance-chart">
              {performanceData.length === 0 ? <div className="merchant-detail-empty-state">No records</div> : <>
                  <div className="merchant-detail-performance-scale-wrapper">
                    <div className="merchant-detail-performance-scale-spacer"></div>
                    <div className="merchant-detail-performance-scale">
                      <span className="merchant-detail-scale-label">0</span>
                      <span className="merchant-detail-scale-label">20</span>
                      <span className="merchant-detail-scale-label">40</span>
                      <span className="merchant-detail-scale-label">60</span>
                      <span className="merchant-detail-scale-label">80</span>
                      <span className="merchant-detail-scale-label">100</span>
                    </div>
                    <div className="merchant-detail-performance-scale-spacer"></div>
                  </div>
                  <div className="merchant-detail-performance-bars-container">
                    {performanceData.map((item, index) => <div key={index} className="merchant-detail-performance-row">
                        <span className="merchant-detail-performance-label">{item.name}</span>
                        <div className="merchant-detail-performance-bar-wrapper">
                          <div className="merchant-detail-performance-bar-container">
                            <div className="merchant-detail-performance-bar" style={{
                          width: `${Math.min(100, item.value / performanceScaleMax * 100)}%`,
                          backgroundColor: item.color
                        }} />
                          </div>
                          <span className="merchant-detail-performance-value">{item.value}</span>
                        </div>
                      </div>)}
                  </div>
                </>}
            </div>
          </div>
        </div>

        {/* Inventory Section */}
        <div className="merchant-detail-section-card merchant-detail-inventory-section" ref={inventorySectionRef}>
          <div className="merchant-detail-inventory-header">
            <div className="merchant-detail-inventory-header-left">
              <h2 className="merchant-detail-section-title">Inventory</h2>
            </div>
            <div className="merchant-detail-inventory-header-right">
              <select className="merchant-detail-category-select" value={inventoryFilter} onChange={e => {
                setInventoryFilter(e.target.value);
                setInventoryTablePage(1);
              }} style={{
                marginRight: 12
              }}>
                <option value="ALL">All Availability</option>
                <option value="IN_STOCK">In Stock</option>
                <option value="LOW_STOCK">Low Stock</option>
                <option value="OUT_OF_STOCK">Out of Stock</option>
              </select>
              <button className="merchant-detail-add-item-btn" type="button" onClick={() => setIsAddInventoryItemModalOpen(true)}>
                <Plus size={18} />
                Add Item
              </button>
              <button className="merchant-detail-import-btn" onClick={() => csvFileInputRef.current?.click()} disabled={importingCsv}>
                <Upload size={18} />
                {importingCsv ? "Importing..." : "Import CSV"}
              </button>
              <input ref={csvFileInputRef} type="file" accept=".csv" style={{
                display: "none"
              }} onChange={handleCsvFileChange} />
            </div>
          </div>
          <div className="inventory-table-wrapper">
            {inventoryLoading ? <div className="inventory-empty-state">Loading inventory...</div> : inventoryFilledData.length === 0 ? <div className="inventory-empty-state">No inventory items</div> : <>
                <table className="inventory-table">
                  <thead>
                    <tr>
                      <th>AUTO-REPLENISHMENT</th>
                      <th>SKU</th>
                      <th>ITEM</th>
                      <th>QTY</th>
                      <th>CATEGORY</th>
                      <th>COST</th>
                      <th>UOM</th>
                      <th>SRP</th>
                      <th>AVAILABILITY</th>
                      <th>STATUS</th>
                      <th>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventoryFilledData.map((row, idx) => {
                    if (row._placeholder) {
                      return <tr key={row.id} className="inventory-row-placeholder">
                            <td colSpan={11}>&nbsp;</td>
                          </tr>;
                    }
                    const {
                      label: statusLabel,
                      badgeClass: statusBadgeClass
                    } = getStatusBadge(row.status);
                    const availBadge = row.availability === "In Stock" ? "merchant-detail-availability in-stock" : row.availability === "Low Stock" ? "merchant-detail-availability low-stock" : "merchant-detail-availability out-of-stock";
                    const s = String(row.status ?? "").toUpperCase();
                    return <tr key={row.id ?? idx} onClick={() => setEditingInventoryItem(row)} style={{
                      cursor: "pointer"
                    }}>
                          <td onClick={e => e.stopPropagation()}>
                            <label className="merchant-detail-toggle">
                              <input type="checkbox" checked={row.autoReplenishment} disabled={inventoryUpdating === (row.inventoryId ?? row.id)} onChange={() => {
                            handleInventoryUpdate(row, {
                              stockAmount: row.qty ?? 0,
                              criticalLevel: row.criticalLevel ?? 0,
                              replenishAmount: row.replenishAmount ?? 0,
                              isAutoReplenishEnabled: !row.autoReplenishment
                            });
                          }} className="merchant-detail-toggle-input" />
                              <span className="merchant-detail-toggle-slider"></span>
                            </label>
                          </td>
                          <td>{row.sku || "-"}</td>
                          <td>{row.item || "-"}</td>
                          <td>{row.qty}</td>
                          <td>{row.category || "-"}</td>
                          <td className="inventory-cell-currency">{formatCurrency(row.cost)}</td>
                          <td>{row.uom || "-"}</td>
                          <td className="inventory-cell-currency">{formatCurrency(row.srp)}</td>
                          <td>
                            <button type="button" className={availBadge} style={{
                          cursor: "pointer",
                          background: "none",
                          border: "none",
                          padding: 0
                        }} onClick={e => {
                          e.stopPropagation();
                          const v = String(row.availability);
                          if (v === "Low Stock") {
                            setInventoryFilter(prev => prev === "LOW_STOCK" ? "ALL" : "LOW_STOCK");
                          } else if (v === "Out of Stock") {
                            setInventoryFilter(prev => prev === "OUT_OF_STOCK" ? "ALL" : "OUT_OF_STOCK");
                          } else {
                            setInventoryFilter("ALL");
                          }
                          setInventoryTablePage(1);
                          scrollToInventory();
                        }}>
                              {row.availability}
                            </button>
                          </td>
                          <td><span className={statusBadgeClass}>{statusLabel}</span></td>
                          <td onClick={e => e.stopPropagation()}>
                            <div className="merchant-detail-inventory-actions">
                              {s !== "ACTIVE" && <>
                                  <button type="button" className="merchant-detail-action-link" disabled={inventoryUpdating === (row.inventoryId ?? row.id)} onClick={() => handleInventoryStatusChange(row, "ACTIVE")}>
                                    Activate
                                  </button>
                                </>}
                              {s === "ACTIVE" && <>
                                  <button type="button" className="merchant-detail-action-link" disabled={inventoryUpdating === (row.inventoryId ?? row.id)} onClick={() => handleInventoryStatusChange(row, "INACTIVE")}>
                                    Deactivate
                                  </button>
                                </>}
                            </div>
                          </td>
                        </tr>;
                  })}
                  </tbody>
                </table>

                <div style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                padding: "16px 0",
                marginTop: 16,
                borderTop: "1px solid #e5e7eb",
                gap: 8
              }}>
                  <span style={{
                  fontSize: "0.875rem",
                  color: "#374151"
                }}>
                    {inventoryRangeStart}-{inventoryRangeEnd} of {inventoryTotalCount}
                  </span>
                  <button type="button" disabled={inventorySafePage <= 1} onClick={() => setInventoryTablePage(p => Math.max(1, (Number(p) || 1) - 1))} style={{
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: 4,
                  backgroundColor: "#ffffff",
                  color: "#374151",
                  fontSize: "0.875rem",
                  cursor: inventorySafePage <= 1 ? "not-allowed" : "pointer",
                  opacity: inventorySafePage <= 1 ? 0.5 : 1
                }} aria-label="Previous page">
                    ‹
                  </button>
                  <button type="button" disabled={inventorySafePage >= inventoryTotalPages} onClick={() => setInventoryTablePage(p => Math.min(inventoryTotalPages, (Number(p) || 1) + 1))} style={{
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: 4,
                  backgroundColor: "#ffffff",
                  color: "#374151",
                  fontSize: "0.875rem",
                  cursor: inventorySafePage >= inventoryTotalPages ? "not-allowed" : "pointer",
                  opacity: inventorySafePage >= inventoryTotalPages ? 0.5 : 1
                }} aria-label="Next page">
                    ›
                  </button>
                </div>
              </>}
          </div>
        </div>

        {/* Top Selling Items Section */}
        <div className="merchant-detail-section-card">
          <div className="merchant-detail-section-header">
            <h2 className="merchant-detail-section-title">Top Selling Items</h2>
          </div>
          {topSellingLoading ? <div className="merchant-detail-empty-state">Loading...</div> : topSellingItems.length === 0 ? <div className="merchant-detail-empty-state">No records</div> : <div className="merchant-detail-items-grid">
              {topSellingItems.map(item => <div key={item.id} className="merchant-detail-item-card">
                  <div className="merchant-detail-item-content">
                    <img src={item.image || smallNoImage} alt={item.name} className="merchant-detail-item-image" onError={e => {
                  if (e.target.src !== smallNoImage) {
                    e.target.src = smallNoImage;
                  }
                }} />
                    <div className="merchant-detail-item-info">
                      <h3 className="merchant-detail-item-name">{item.name}</h3>
                      {item.sold != null && <span className="merchant-detail-item-sold">Sold: {typeof item.sold === "number" ? item.sold.toLocaleString() : item.sold}</span>}
                      {item.price && <span className="merchant-detail-item-price">{typeof item.price === "number" ? `PHP ${item.price.toLocaleString("en-PH", {
                      minimumFractionDigits: 2
                    })}` : item.price}</span>}
                    </div>
                  </div>
                </div>)}
            </div>}
        </div>

        {/* Suggested Items Section */}
        <div className="merchant-detail-section-card">
          <div className="merchant-detail-section-header">
            <h2 className="merchant-detail-section-title">Suggested Items</h2>
            <button className="merchant-detail-add-item-btn" onClick={() => setIsAddItemModalOpen(true)}>
              <Plus size={18} />
              Add Item
            </button>
          </div>
          <div className="merchant-detail-items-grid">
            {suggestedItemsLoading ? <div className="merchant-detail-empty-state">Loading suggested items...</div> : suggestedItems.length === 0 ? <div className="merchant-detail-empty-state">No suggested items</div> : suggestedItems.map(item => <div key={item.id} className="merchant-detail-item-card">
                <div className="merchant-detail-item-actions">
                  <button type="button" className="merchant-detail-item-action-btn" title="Remove suggestion" onClick={e => {
                  e.stopPropagation();
                  handleUnsuggestProduct(item.productId ?? item.inventoryId ?? item.id);
                }}>
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="merchant-detail-item-content">
                  <img src={item.image || smallNoImage} alt={item.name} className="merchant-detail-item-image" onError={e => {
                  if (e.target.src !== smallNoImage) {
                    e.target.src = smallNoImage;
                  }
                }} />
                  <div className="merchant-detail-item-info">
                    <h3 className="merchant-detail-item-name">{item.name}</h3>
                    <div className="merchant-detail-item-details">
                      <span className="merchant-detail-item-sku">{item.sku}</span>
                      <span className="merchant-detail-item-unit">{item.unit}</span>
                    </div>
                    <span className="merchant-detail-item-price">{item.price}</span>
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {/* Transaction History Section */}
        <div className="merchant-detail-section-card">
          <h2 className="merchant-detail-section-title">Transaction History</h2>
          {transactionLoading ? <div className="merchant-detail-empty-state">Loading...</div> : transactionHistory.length === 0 ? <div className="merchant-detail-empty-state">No records</div> : <div className="merchant-detail-transaction-list">
            {transactionHistory.map(transaction => <div key={transaction.id} className="merchant-detail-transaction-item">
                <div className="merchant-detail-transaction-info">
                  <div className="merchant-detail-transaction-main">
                    <span className="merchant-detail-transaction-item-name">{transaction.itemName}</span>
                    <span className={`merchant-detail-transaction-amount ${transaction.isNegative ? 'negative' : ''}`}>
                      {transaction.amount}
                    </span>
                  </div>
                  <div className="merchant-detail-transaction-secondary">
                    <span className="merchant-detail-transaction-id">{transaction.transactionId}</span>
                    <span className="merchant-detail-transaction-date">{transaction.date}</span>
                  </div>
                </div>
              </div>)}
          </div>}
        </div>
        </div>
      </div>

      {/* Markup Setting Modal */}
      <MarkupSettingModal isOpen={isMarkupModalOpen} onClose={() => setIsMarkupModalOpen(false)} />

      {/* Edit Merchant Inventory Modal */}
      <EditMerchantInventoryModal item={editingInventoryItem} isOpen={!!editingInventoryItem} onClose={() => setEditingInventoryItem(null)} onSave={handleInventoryUpdate} />

      <AddItemModal isOpen={isAddItemModalOpen} onClose={() => setIsAddItemModalOpen(false)} onAdd={handleAddItems} merchantId={merchantId} searchItems={inventoryData} />

      <AddInventoryItemModal isOpen={isAddInventoryItemModalOpen} onClose={() => setIsAddInventoryItemModalOpen(false)} onAdd={handleAddInventoryItems} products={allProductsLoading ? [] : allProducts} maxResults={5} />

      <OrderCutoffScheduleModal isOpen={isCutoffModalOpen} onClose={() => {
      setIsCutoffModalOpen(false);
      setSelectedCutoffDay(null);
    }} day={selectedCutoffDay} scheduleData={scheduleData[selectedCutoffDay]} onSave={(day, items) => {
      setScheduleData(prev => ({
        ...prev,
        [day]: items
      }));
      if (merchantId && day) {
        const isActiveDay = !!scheduleEnabledByDay[day];
        const status = isActiveDay ? "ACTIVE" : "INACTIVE";
        const cutoffEntry = scheduleData[day];
        if (cutoffEntry && cutoffEntry.id) {
          // Existing record for this day -> PUT /merchant-cutoff/{id}
          adminServices.setMerchantCutoffs(cutoffEntry.id, day, items, status).catch(err => {
            console.error("Failed to update merchant cut-off settings:", err);
          });
        } else {
          // No record yet for this day -> POST /merchant-cutoff/{merchantId}
          adminServices.createMerchantCutoffs(merchantId, day, items, status).then(res => {
            const created = res?.data;
            if (created && created.day) {
              setScheduleData(prev => ({
                ...prev,
                [created.day]: created
              }));
            }
          }).catch(err => {
            console.error("Failed to create merchant cut-off settings:", err);
          });
        }
      }
    }} />

      {isChangePasswordOpen && <div className="jmodal-overlay" onClick={e => {
      if (e.target === e.currentTarget) closeChangePassword();
    }}>
          <div className="jmodal-content" style={{
        minWidth: 520,
        textAlign: "left"
      }}>
            <h2 style={{
          marginTop: 0,
          marginBottom: 16,
          textAlign: "center"
        }}>Change Password</h2>

            <form onSubmit={handleChangePassword}>
              <div className="modalFormRow" style={{
            height: "auto"
          }}>
                <label style={{
              display: "block",
              width: "100%",
              marginBottom: 6
            }}>New Password</label>
                <div style={{
              position: "relative"
            }}>
                  <input type={showNewPassword ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)} autoFocus style={{
                width: "100%",
                height: 36,
                paddingRight: 44
              }} />
                  <button type="button" onClick={() => setShowNewPassword(s => !s)} aria-label={showNewPassword ? "Hide password" : "Show password"} style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "transparent",
                border: "none",
                padding: 0,
                margin: 0,
                cursor: "pointer",
                lineHeight: 0,
                color: "#6b7280"
              }}>
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="modalFormRow" style={{
            height: "auto",
            marginTop: 10
          }}>
                <label style={{
              display: "block",
              width: "100%",
              marginBottom: 6
            }}>Confirm New Password</label>
                <div style={{
              position: "relative"
            }}>
                  <input type={showConfirmNewPassword ? "text" : "password"} value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} style={{
                width: "100%",
                height: 36,
                paddingRight: 44,
                borderColor: passwordMismatch ? "orangered" : undefined
              }} />
                  <button type="button" onClick={() => setShowConfirmNewPassword(s => !s)} aria-label={showConfirmNewPassword ? "Hide password" : "Show password"} style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "transparent",
                border: "none",
                padding: 0,
                margin: 0,
                cursor: "pointer",
                lineHeight: 0,
                color: "#6b7280"
              }}>
                    {showConfirmNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {passwordMismatch && <div style={{
              color: "orangered",
              fontSize: 12,
              marginTop: 6
            }}>
                    Passwords do not match.
                  </div>}
              </div>

              <div style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 18
          }}>
                <button type="button" className="modalButton2" style={{
              backgroundColor: "#0F2A40"
            }} onClick={closeChangePassword} disabled={passwordSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="modalButton1" style={{
              backgroundColor: "#0F2A40"
            }} disabled={passwordSubmitting || !newPassword || !confirmNewPassword || passwordMismatch || !merchantId}>
                  {passwordSubmitting ? "Changing..." : "Change Password"}
                </button>
              </div>
            </form>
          </div>
        </div>}
    </div>;
};
export default MerchantDetail;